/**
 * @CODE:SPEC-ACC-002-COMP-TRAIL-001
 * TrailSelector Component
 *
 * Displays the 4 major Korean trails as interactive cards for accommodation search filtering.
 * Matches the design pattern from CourseSearch.tsx
 *
 * Features:
 * - 4 trail cards: 해파랑길, 남파랑길, 서해랑길, DMZ 평화의 길
 * - Visual feedback for selected trail
 * - Responsive grid layout (1/2/4 columns)
 * - Icon-based trail identification
 */

import { Waves, Mountain, Sunset, Shield } from "lucide-react";
import { TRAIL_NAMES, getAllTrails } from "@/constants/trails";

interface TrailSelectorProps {
  /** Currently selected trail ID */
  selectedTrailId?: string;
  /** Callback when a trail is selected */
  onTrailSelect: (trailId: string) => void;
}

/** Trail card configuration with ID, display name, icon, color theme */
const TRAIL_CARDS = [
  {
    id: "T_THEME_MNG0000011235", // 해파랑길
    name: TRAIL_NAMES["T_THEME_MNG0000011235"],
    courseCount: "50개 코스",
    distance: "750km",
    description: "동해안의 푸른 바다와 함께 걷는 길",
    icon: Waves,
    colorTheme: {
      selected: "bg-blue-500 text-white shadow-lg scale-105",
      default: "bg-white hover:bg-blue-50 border border-gray-200 hover:shadow-md hover:scale-102",
      iconBgSelected: "bg-white/20",
      iconBgDefault: "bg-blue-100 group-hover:bg-blue-200",
      iconColorSelected: "text-white",
      iconColorDefault: "text-blue-600",
    },
  },
  {
    id: "T_ROUTE_MNG0000000001", // 남파랑길
    name: TRAIL_NAMES["T_ROUTE_MNG0000000001"],
    courseCount: "91개 코스",
    distance: "1,463km",
    description: "남쪽의 쪽빛 바다와 함께 걷는 길",
    icon: Mountain,
    colorTheme: {
      selected: "bg-emerald-500 text-white shadow-lg scale-105",
      default: "bg-white hover:bg-emerald-50 border border-gray-200 hover:shadow-md hover:scale-102",
      iconBgSelected: "bg-white/20",
      iconBgDefault: "bg-emerald-100 group-hover:bg-emerald-200",
      iconColorSelected: "text-white",
      iconColorDefault: "text-emerald-600",
    },
  },
  {
    id: "T_ROUTE_MNG0000000043", // 서해랑길
    name: TRAIL_NAMES["T_ROUTE_MNG0000000043"],
    courseCount: "108개 코스",
    distance: "1,804km",
    description: "서쪽 바다의 갯벌과 함께 걷는 길",
    icon: Sunset,
    colorTheme: {
      selected: "bg-amber-500 text-white shadow-lg scale-105",
      default: "bg-white hover:bg-amber-50 border border-gray-200 hover:shadow-md hover:scale-102",
      iconBgSelected: "bg-white/20",
      iconBgDefault: "bg-amber-100 group-hover:bg-amber-200",
      iconColorSelected: "text-white",
      iconColorDefault: "text-amber-600",
    },
  },
  {
    id: "T_ROUTE_MNG0000000047", // DMZ 평화의 길
    name: TRAIL_NAMES["T_ROUTE_MNG0000000047"],
    courseCount: "48개 코스",
    distance: "553km",
    description: "분단의 아픔을 딛고 평화로 나아가는 길",
    icon: Shield,
    colorTheme: {
      selected: "bg-purple-500 text-white shadow-lg scale-105",
      default: "bg-white hover:bg-purple-50 border border-gray-200 hover:shadow-md hover:scale-102",
      iconBgSelected: "bg-white/20",
      iconBgDefault: "bg-purple-100 group-hover:bg-purple-200",
      iconColorSelected: "text-white",
      iconColorDefault: "text-purple-600",
    },
  },
];

/**
 * TrailSelector - 4대 둘레길 선택 카드
 *
 * @example
 * ```tsx
 * <TrailSelector
 *   selectedTrailId="T_THEME_MNG0000011235"
 *   onTrailSelect={(trailId) => console.log('Selected:', trailId)}
 * />
 * ```
 */
export default function TrailSelector({
  selectedTrailId,
  onTrailSelect,
}: TrailSelectorProps) {
  return (
    <div className="bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            4대 코리아둘레길
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            각각의 둘레길은 고유한 매력과 특색을 가지고 있습니다. 원하시는 둘레길을 선택하여 특별한 여행을 시작해보세요.
          </p>
        </div>

        {/* Trail Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TRAIL_CARDS.map((trail) => {
            const Icon = trail.icon;
            const isSelected = selectedTrailId === trail.id;
            const theme = trail.colorTheme;

            return (
              <div
                key={trail.id}
                onClick={() => onTrailSelect(trail.id)}
                className={`p-5 rounded-xl cursor-pointer transition-all duration-300 group ${
                  isSelected ? theme.selected : theme.default
                }`}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={`${trail.name} 선택`}
              >
                {/* Icon and Trail Info */}
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isSelected ? theme.iconBgSelected : theme.iconBgDefault
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 ${
                        isSelected ? theme.iconColorSelected : theme.iconColorDefault
                      }`}
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">{trail.name}</h3>
                    <div className="text-xs opacity-75">
                      {trail.courseCount} · {trail.distance}
                    </div>
                  </div>
                </div>

                {/* Trail Description */}
                <p className="text-sm opacity-90 leading-relaxed">
                  {trail.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
